import { Divider, SvgIcon, Typography } from '@mui/material';
import { Cog as SettingsIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { ProductIcon } from '../../Components/icons/products/ProductIcon';
import { DATA_OG, DATA_TEST, getDynamicDataOGId, getDynamicDataTestId } from '../../enums';
import { IProductDetails, MUI_PRODUCT_ICONS, PRODUCT_ICONS, PRODUCT_KEY } from '../../types';

import { ContextSwitcherListItem } from './ContextSwitcherListItem';

interface IAppSwitcherProps {
  activeProductKey?: PRODUCT_KEY;
  entitledApplications: IProductDetails[];
  mobile?: boolean;
  autoFocusFirstItem?: boolean;
  firstContextSwitcherItemRef?: React.MutableRefObject<HTMLLIElement>;
  anchorEl?: HTMLDivElement;
  handleClose?(): void;
}

export const AppSwitcherListItems: React.FC<IAppSwitcherProps> = ({
  activeProductKey,
  mobile,
  entitledApplications,
  autoFocusFirstItem = true,
  firstContextSwitcherItemRef,
  anchorEl,
  handleClose
}) => {
  const moveAdminToLastIndex = (adminIdx: number) => {
    const [item] = entitledApplications.splice(adminIdx, 1);
    entitledApplications.push(item);
  };

  const getProductIcon = (productKey: string) => {
    return PRODUCT_ICONS[productKey.toLowerCase()] || MUI_PRODUCT_ICONS[productKey.toLowerCase()];
  };

  const adminSettingsIndex = entitledApplications?.findIndex(
    (app) => app.entitlementProductKey === MUI_PRODUCT_ICONS.adminsettings
  );

  // Makes sure that the admin settings should always be the last item in the list to align with the design
  if (adminSettingsIndex > -1) {
    moveAdminToLastIndex(adminSettingsIndex);
  }

  // Move active product to the top of the list
  // Using slice to avoid mutating the original array
  const activeApplicationSortedFirst = entitledApplications
    ?.slice()
    .sort((a) => (a.entitlementProductKey.toLowerCase() === activeProductKey?.toLowerCase() ? -1 : null));

  const activeApplicationRemovedForMobile = activeApplicationSortedFirst.filter(
    (product) => product.entitlementProductKey.toLowerCase() !== activeProductKey?.toLowerCase()
  );

  return (
    <>
      {entitledApplications?.length > 0 &&
        (mobile ? activeApplicationRemovedForMobile : activeApplicationSortedFirst).map((product, index, self) => {
          const productIcon = getProductIcon(product.entitlementProductKey);

          const isFirstItem = index === 0;
          const isLastItem = index === self.length - 1;
          const isAdminItem = productIcon === MUI_PRODUCT_ICONS.adminsettings;
          const showDivider = entitledApplications?.length >= 2 && isAdminItem;

          const firstNonActiveIndex = self.findIndex(
            (item) => item.entitlementProductKey.toLowerCase() !== activeProductKey?.toLowerCase()
          );

          return (
            <React.Fragment key={product.entitlementProductKey}>
              {showDivider && (
                <Divider
                  component="li"
                  sx={{ m: '0 !important' }}
                  aria-hidden="true"
                  data-test={DATA_TEST.admin_settings_divider}
                  key={`admin-divider`}
                />
              )}
              <ContextSwitcherListItem
                firstContextSwitcherItemRef={firstContextSwitcherItemRef}
                anchorEl={anchorEl}
                active={product.entitlementProductKey.toLowerCase() === activeProductKey?.toLowerCase()}
                key={product.entitlementProductKey}
                openInNewTab={product.openInNewTab}
                url={product.url}
                styleOverrides={{
                  mb: isLastItem ? 0 : 0.5,
                  mt: isFirstItem || (showDivider && isAdminItem) ? 0.5 : 0
                }}
                tooltipText={product.description}
                dataTest={getDynamicDataTestId(DATA_TEST.app_switcher_item, product.entitlementProductKey)}
                autoFocus={autoFocusFirstItem && index === firstNonActiveIndex}
                dataOg={getDynamicDataOGId(DATA_OG.app_switcher_item, product.entitlementProductKey)}
                handleClose={handleClose}
              >
                <SvgIcon
                  viewBox="0 0 16 16"
                  sx={{
                    svg: {
                      fill: 'inherit'
                    },
                    fontSize: '16px'
                  }}
                >
                  {productIcon !== MUI_PRODUCT_ICONS.adminsettings ? (
                    <ProductIcon icon={productIcon} />
                  ) : (
                    <SettingsIcon data-test={DATA_TEST.admin_settings_icon} />
                  )}
                </SvgIcon>
                <Typography variant="bodyDefault" component="span">
                  {product?.displayName?.replace(' and ', ' & ')}
                </Typography>
              </ContextSwitcherListItem>
            </React.Fragment>
          );
        })}
    </>
  );
};
